import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useImpersonation } from '@/features/auth';
import { useCreateCandidate } from '../hooks/useCreateCandidate';
import { CandidateFormFields, type CandidateFormData } from '../components/CandidateFormFields';
import { useState } from 'react';

export function CandidateNewPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { effectiveCompanyId } = useImpersonation();
  const { createCandidate, isLoading } = useCreateCandidate();
  const [country, setCountry] = useState<string | null>(searchParams.get('country'));

  const defaultValues: CandidateFormData = {
    name: searchParams.get('name') ?? '',
    email: searchParams.get('email') ?? '',
    country: searchParams.get('country'),
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CandidateFormData>({ defaultValues });

  const onSubmit = handleSubmit((data) => {
    if (!effectiveCompanyId) {
      toast.error('Company ID not found');
      return;
    }
    createCandidate(
      {
        name: data.name,
        email: data.email,
        country: country || undefined,
        phone: data.phone || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Candidate created successfully');
          navigate(-1);
        },
        onError: () => toast.error('Failed to create candidate'),
      }
    );
  });

  return (
    <div className="container mx-auto py-6">
      <Card className="max-w-lg mx-auto">
        <CardHeader>
          <CardTitle>Add Candidate</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <CandidateFormFields
              register={register}
              errors={errors}
              onCountryChange={setCountry}
              defaultCountry={country}
            />
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
